// ─── Single react-query v3 client ──────────────────────────────────────────────
// Mounted at App root via QueryClientProvider. First react-query consumer in the
// repo (F1.1.d). staleTime keeps the Jobs panel quiet while the operator moves
// around the booking.

import { QueryClient } from 'react-query';
import { isTransientApiError } from './transientErrors';

// How many times a *transient* failure is retried before the error surfaces.
// Two is enough to ride out an origin restart or a redeploy without making a
// genuinely-down API feel like a hang.
const TRANSIENT_RETRY_LIMIT = 2;

// Retries are off for anything the backend actually answered: most API failures
// here (404, 409, 422) are user-fixable and auto-retrying just hides the real
// error. But that blanket `retry: false` also swallowed the case it was never
// meant to cover — a request that got NO response at all. One dropped request
// was then terminal for every query on screen at once, which is how a momentary
// API blip emptied the whole Jobs panel and every picker in the job dialog.
//
// So: retry only what `isTransientApiError` recognises (no response / timeout),
// and keep failing fast on everything the server had an opinion about.
function retryTransientOnly(failureCount: number, error: unknown): boolean {
  return isTransientApiError(error) && failureCount < TRANSIENT_RETRY_LIMIT;
}

// 400ms, then 800ms. Deliberately short — this is a foreground fetch with an
// operator watching a spinner, not a background sync.
function transientRetryDelay(attemptIndex: number): number {
  return 400 * 2 ** attemptIndex;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: retryTransientOnly,
      retryDelay: transientRetryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Mutations stay at zero retries even for transient failures: a create or
      // update that timed out may well have been applied server-side, and a
      // silent replay would double-post it. The user re-submits instead.
      retry: false,
    },
  },
});
